import React from 'react';
import '../../../assets/css/Buyer/agents/pagination.css';

/**
 * Reusable Pagination Component
 *
 * Props:
 * @param {number}   currentPage   - Active page number (1-based)
 * @param {number}   totalPages    - Total number of pages
 * @param {function} onPageChange  - Callback: (pageNumber) => void
 * @param {number}   [siblingCount=1] - How many page buttons to show on each side of active
 */
const Pagination = ({ currentPage, totalPages, onPageChange, siblingCount = 1 }) => {
  if (totalPages <= 1) return null;

  // ── Generate page number array with "..." dots ──────────────
  const getPageNumbers = () => {
    const totalNumbers = siblingCount * 2 + 3; // siblings + current + 2 edges
    const totalBlocks  = totalNumbers + 2;      // + 2 dots slots

    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSiblingIndex  = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const showLeftDots  = leftSiblingIndex > 2;
    const showRightDots = rightSiblingIndex < totalPages - 1;

    if (!showLeftDots && showRightDots) {
      const leftRange = Array.from({ length: 3 + siblingCount * 2 }, (_, i) => i + 1);
      return [...leftRange, '...', totalPages];
    }

    if (showLeftDots && !showRightDots) {
      const rightRange = Array.from(
        { length: 3 + siblingCount * 2 },
        (_, i) => totalPages - (3 + siblingCount * 2) + 1 + i
      );
      return [1, '...', ...rightRange];
    }

    const middleRange = Array.from(
      { length: rightSiblingIndex - leftSiblingIndex + 1 },
      (_, i) => leftSiblingIndex + i
    );
    return [1, '...', ...middleRange, '...', totalPages];
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav className="pag-wrapper" aria-label="Pagination">
      {/* Previous Button */}
      <button
        className="pag-btn pag-arrow"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        aria-label="Previous page"
      >
        &lt;
      </button>

      {/* Page Numbers */}
      {pageNumbers.map((page, index) =>
        page === '...' ? (
          <span key={`dots-${index}`} className="pag-dots">…</span>
        ) : (
          <button
            key={page}
            className={`pag-btn ${currentPage === page ? 'pag-active' : ''}`}
            onClick={() => onPageChange(page)}
            aria-label={`Page ${page}`}
            aria-current={currentPage === page ? 'page' : undefined}
          >
            {page}
          </button>
        )
      )}

      {/* Next Button */}
      <button
        className="pag-btn pag-arrow"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        aria-label="Next page"
      >
        &gt;
      </button>
    </nav>
  );
};

export default Pagination;